import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X } from 'lucide-react';
import * as help from '@/lib/helpStore';

function useHelpState() {
  const [s, setS] = useState(help.getHelpState());
  useEffect(() => {
    // Sync any state that resolved between initial read and subscription (avoids missed emits).
    setS(help.getHelpState());
    return help.subscribe(setS);
  }, []);
  return s;
}

function anyBlockingDialogOpen() {
  // Defer to workflow/critical dialogs (Radix open dialogs, idle warning). Our own panel is excluded.
  const nodes = document.querySelectorAll('[role="dialog"][data-state="open"], [data-testid="idle-warning-dialog"]');
  for (const n of nodes) {
    if (n.closest('[data-help-surface="orientation"]')) continue;
    return true;
  }
  return false;
}

function isTyping() {
  const el = document.activeElement;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

/** Single app-level guidance controller: orientation panel + Help & Tips dialog. */
export default function HelpAndTips({ user }) {
  const role = user?.role || null;
  const account = user?.user_id || user?.id || user?.email || null;
  const s = useHelpState();
  const [orientOpen, setOrientOpen] = useState(false);
  const [blocked, setBlocked] = useState(false); // another dialog currently has priority
  const [view, setView] = useState('welcome'); // 'welcome' | 'tour'
  const [replayKsa, setReplayKsa] = useState(false);
  const lastRestart = useRef(s.restartNonce);

  // Load help state as soon as the authenticated account is available (keyed by account).
  useEffect(() => { if (account && role) help.loadHelp(role, account); }, [account, role]);

  // Manual "Restart orientation" trigger (not gated by auto-tips).
  useEffect(() => {
    if (s.restartNonce !== lastRestart.current) {
      lastRestart.current = s.restartNonce;
      setView('welcome');
      setOrientOpen(true);
    }
  }, [s.restartNonce]);

  // Auto orientation: evaluate reactively once the account + prefs are ready (no polling/latch).
  // Re-runs on any store emit; the store's orientationShownSession flag prevents repeats this
  // session and is cleared on logout/account change. Defers past blocking dialogs / typing.
  useEffect(() => {
    if (orientOpen) return;
    if (!role || !s.loaded || s.account !== account) return;
    if (!help.autoTipsOn() || help.orientationDone(role) || s.orientationShownSession) return;
    if (anyBlockingDialogOpen() || isTyping()) return; // defer; re-evaluated on next store emit
    help.markOrientationShownSession();
    setView('welcome');
    setOrientOpen(true);
  }, [role, account, s.loaded, s.account, s.orientationShownSession, s.prefs, orientOpen]);

  const onSkip = useCallback(() => { setOrientOpen(false); if (role) help.dismissOrientation(role); }, [role]);
  const onGotIt = useCallback(() => { setOrientOpen(false); if (role) help.completeOrientation(role); }, [role]);

  // While orientation is open, watch for any competing dialog (Radix open dialog / idle warning).
  // When one takes priority, hide both the panel and the dim backdrop WITHOUT dismissing the
  // orientation intent (orientOpen stays true), so it reappears once the competing dialog closes.
  useEffect(() => {
    if (!orientOpen) { setBlocked(false); return; }
    const check = () => setBlocked(anyBlockingDialogOpen());
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => mo.disconnect();
  }, [orientOpen]);

  // "Actually visible" = open AND not deferred to a competing dialog. The backdrop renders only here.
  const orientVisible = orientOpen && !blocked;

  const content = s.content;
  const intro = content?.intro || 'Use the menu to move between your tools.';
  const tourTitle = role === 'job_seeker' ? 'Your transition tools' : 'Getting around';

  return (
    <>
      {/* ORIENTATION — non-modal compact bottom panel; page stays visible/usable above it. */}
      {orientVisible && (
        <>
          {/* Subtle page dim behind orientation only. pointer-events-none: never intercepts
              clicks/scroll/focus/keyboard. z-40 sits below Radix dialog overlays (z-50) so any
              competing dialog renders above and the backdrop is gone the moment orientation hides. */}
          <div
            data-testid="help-orientation-backdrop"
            aria-hidden="true"
            className="fixed inset-0 z-40 bg-black/20 pointer-events-none"
          />
          <div
            data-help-surface="orientation"
            data-testid="help-orientation-panel"
            role="dialog"
            aria-label="Welcome to Sergeant TAP"
            className="fixed z-[70] left-1/2 -translate-x-1/2 bottom-0 sm:bottom-6 w-full sm:w-[440px] max-w-[96vw]
                       bg-white border border-slate-200 shadow-2xl rounded-t-2xl sm:rounded-2xl p-5
                       pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-5"
            onKeyDown={(e) => { if (e.key === 'Escape') onSkip(); }}
          >
            <button
              data-testid="help-orientation-close"
              onClick={onSkip}
              aria-label="Dismiss"
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
            {view === 'welcome' ? (
              <>
                <h2 className="text-lg font-bold text-[#222] mb-2">Welcome to Sergeant TAP</h2>
                <div className="text-sm text-slate-600 leading-relaxed space-y-1">
                  <p>Let&apos;s get you comfortable.</p>
                  <p>Use the menu to move between your tools.</p>
                  <p>We&apos;ll explain useful features as you reach them.</p>
                  <p>You can revisit any explanation in Help &amp; Tips.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button data-testid="help-show-around-btn" onClick={() => setView('tour')} className="sm:flex-1">Show me around</Button>
                  <Button data-testid="help-skip-btn" variant="outline" onClick={onSkip} className="sm:flex-1">Skip</Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-[#222] mb-2">{tourTitle}</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{intro}{role === 'job_seeker' ? ' Choose where to start from the menu. We\u2019ll offer a little guidance along the way.' : ''}</p>
                <div className="flex mt-4">
                  <Button data-testid="help-got-it-btn" onClick={onGotIt} className="w-full sm:w-auto">Got it</Button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* HELP & TIPS — standard dialog (opened from the menu). */}
      <Dialog open={s.helpOpen} onOpenChange={(o) => (o ? help.openHelp() : help.closeHelp())}>
        <DialogContent data-testid="help-tips-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Help &amp; Tips</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Show automatic tips</p>
                <p className="text-xs text-slate-500">Turn off to stop unsolicited orientation and tips. Manual help stays available.</p>
              </div>
              <Switch data-testid="help-auto-tips-toggle" checked={help.autoTipsOn()} onCheckedChange={(v) => help.setAutoTips(v)} />
            </div>

            {content?.intro && <p className="text-sm text-slate-600 leading-relaxed">{content.intro}</p>}

            {(content?.sections || []).map((sec) => (
              <div key={sec.id} data-testid={`help-section-${sec.id}`}>
                <h3 className="text-sm font-semibold text-slate-900">{sec.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{sec.body}</p>
              </div>
            ))}

            <div className="pt-1 border-t border-slate-100 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                {role === 'job_seeker' && content?.ksa_help && (
                  <Button data-testid="help-replay-ksa-btn" variant="outline" size="sm" onClick={() => setReplayKsa((v) => !v)}>
                    {replayKsa ? 'Hide KSA explanation' : 'Replay KSA explanation'}
                  </Button>
                )}
                <Button data-testid="help-restart-orientation-btn" variant="outline" size="sm" onClick={() => help.restartAllGuidance(role)}>
                  Restart all guidance
                </Button>
              </div>
              <p data-testid="help-restart-caption" className="text-xs text-slate-500 leading-relaxed">
                Restarts the welcome and shows tips again as you reach each feature. Turns automatic tips back on.
              </p>
            </div>

            {replayKsa && role === 'job_seeker' && content?.ksa_help && (
              <div data-testid="help-ksa-replay" className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-900">{content.ksa_help.title}</p>
                <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{content.ksa_help.body}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
